// @flow

import _ from 'lodash/fp';
import {combineReducers} from 'redux';
import {
  GET_REQUEST, GET_SUCCESS,
  GET_FAILURE, CREATE_REQUEST,
  CREATE_SUCCESS, CREATE_FAILURE,
  UPDATE_REQUEST, UPDATE_SUCCESS,
  UPDATE_FAILURE, PROJECT_GET_REQUEST,
  PROJECT_GET_SUCCESS, PROJECT_GET_FAILURE,
  DEVIATIONS_REQUEST, DEVIATIONS_SUCCESS,
  DEVIATIONS_FAILURE, OPEN_DEVIATION,
  CLOSE_DEVIATION, ADD_DEVIATION,
  UPDATE_DEVIATION, REMOVE_DEVIATION,
  ADD_FILE, REMOVE_FILE, ABORT_FILE_UPLOAD,
  FILE_UPLOADED, CONFIGURATIONS_REQUEST,
  CONFIGURATIONS_SUCCESS, CONFIGURATIONS_FAILURE,
  TOGGLE_COPY_DIALOG, CLEAR,
  RESET, FILE_UPDATE_REQUEST,
  FILE_UPDATE_FAILURE, FILE_UPDATE_SUCCESS,
  TOGGLE_RENAME_DIALOG, DELETE_FILE_REQUEST,
  DELETE_FILE_SUCCESS, DELETE_FILE_FAILURE,
} from '../actions/apartmentView';
import {SET_DIRTY} from '../actions/form';

const orderByArchived = _.orderBy(['archived'], ['desc']);

const apartmentActive = (state = null, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
    case DELETE_FILE_SUCCESS:
      return {
        ...action.apartment,
        oldContracts: orderByArchived(action.apartment.oldContracts),
        oldFloorPlans: orderByArchived(action.apartment.oldFloorPlans),
        oldKitchenPlans: orderByArchived(action.apartment.oldKitchenPlans),
      };
    case GET_REQUEST:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const apartmentError = (state = null, action) => {
  switch (action.type) {
    case GET_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case FILE_UPDATE_FAILURE:
    case DELETE_FILE_FAILURE:
      return action.error;
    case GET_REQUEST:
    case GET_SUCCESS:
    case CREATE_REQUEST:
    case CREATE_SUCCESS:
    case UPDATE_REQUEST:
    case UPDATE_SUCCESS:
    case FILE_UPDATE_REQUEST:
    case FILE_UPDATE_SUCCESS:
    case DELETE_FILE_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const apartmentDeviations = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
      return action.apartment.deviations ? [
        ...action.apartment.deviations,
      ] : [];
    case ADD_DEVIATION:
      return [
        ...state,
        action.deviation,
      ];
    case UPDATE_DEVIATION:
      const index = _.findIndex({id: action.deviationId}, state);
      return [
        ...state.slice(0, index),
        {...state[index], ...action.data},
        ...state.slice(index + 1),
      ];
    case REMOVE_DEVIATION:
      return _.filter(d => d.id !== action.deviationId, state);
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const apartmentFiles = (state = {}, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
      return {
        contract: action.apartment.contract,
        floorPlan: action.apartment.floorPlan,
        kitchenPlan: action.apartment.kitchenPlan,
      };
    case REMOVE_FILE:
      return {
        ...state,
        [action.name]: null,
      };
    case FILE_UPLOADED:
    case FILE_UPDATE_SUCCESS:
      return {
        ...state,
        [action.name]: action.metadata,
      };
    case CLEAR:
      return {};
    default:
      return state;
  }
};

const apartmentIsDirty = (state = false, action) => {
  switch (action.type) {
    case SET_DIRTY:
      return action.form === 'apartment'
        ? action.dirty
        : state;
    case ADD_DEVIATION:
    case UPDATE_DEVIATION:
    case REMOVE_DEVIATION:
      return true;
    case GET_SUCCESS:
    case UPDATE_SUCCESS:
    case CREATE_SUCCESS:
    case RESET:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const apartmentIsFetching = (state = false, action) => {
  switch (action.type) {
    case GET_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case FILE_UPDATE_REQUEST:
    case DELETE_FILE_REQUEST:
      return true;
    case GET_SUCCESS:
    case GET_FAILURE:
    case CREATE_SUCCESS:
    case CREATE_FAILURE:
    case UPDATE_SUCCESS:
    case UPDATE_FAILURE:
    case FILE_UPDATE_SUCCESS:
    case FILE_UPDATE_FAILURE:
    case DELETE_FILE_SUCCESS:
    case DELETE_FILE_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const apartmentUploads = (state = {}, action) => {
  switch (action.type) {
    case ADD_FILE:
      return {
        ...state,
        [action.name]: action.tempId,
      };
    case ABORT_FILE_UPLOAD:
      return _.omitBy(u => u === action.tempId, state);
    case CLEAR:
      return {};
    default:
      return state;
  }
};

const apartmentReducers = combineReducers({
  active: apartmentActive,
  deviations: apartmentDeviations,
  error: apartmentError,
  files: apartmentFiles,
  isDirty: apartmentIsDirty,
  isFetching: apartmentIsFetching,
  uploads: apartmentUploads,
});

const apartment = (state, action) => {
  const newState = apartmentReducers(state, action);

  if (action.type === RESET) {
    return {
      ...newState,
      deviations: newState.active && newState.active.deviations
        ? [...newState.active.deviations]
        : [],
      files: newState.active ? {
        contract: newState.active.contract,
        floorPlan: newState.active.floorPlan,
        kitchenPlan: newState.active.kitchenPlan,
      } : {},
      uploads: {},
    };
  }

  return newState;
};

const projectActive = (state = null, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case PROJECT_GET_SUCCESS:
      return action.project;
    case PROJECT_GET_REQUEST:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const projectError = (state = null, action) => {
  switch (action.type) {
    case PROJECT_GET_FAILURE:
      return action.error;
    case PROJECT_GET_REQUEST:
    case PROJECT_GET_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const projectIsFetching = (state = false, action) => {
  switch (action.type) {
    case PROJECT_GET_REQUEST:
      return true;
    case PROJECT_GET_SUCCESS:
    case PROJECT_GET_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const project = combineReducers({
  active: projectActive,
  error: projectError,
  isFetching: projectIsFetching,
});

const deviationActive = (state = null, action) => {
  switch (action.type) {
    case OPEN_DEVIATION:
      return action.deviation || null;
    case CLOSE_DEVIATION:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const deviationShow = (state = false, action) => {
  switch (action.type) {
    case OPEN_DEVIATION:
      return true;
    case CLOSE_DEVIATION:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const deviation = combineReducers({
  active: deviationActive,
  show: deviationShow,
});

const deviationsError = (state = null, action) => {
  switch (action.type) {
    case DEVIATIONS_FAILURE:
      return action.error;
    case DEVIATIONS_REQUEST:
    case DEVIATIONS_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const deviationsIsFetching = (state = false, action) => {
  switch (action.type) {
    case DEVIATIONS_REQUEST:
      return true;
    case DEVIATIONS_SUCCESS:
    case DEVIATIONS_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const notArchived =
  _.filter({archived: null});

const deviationsList = (state = [], action) => {
  switch (action.type) {
    case DEVIATIONS_SUCCESS:
      return notArchived(action.deviations);
    case DEVIATIONS_REQUEST:
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const deviations = combineReducers({
  error: deviationsError,
  isFetching: deviationsIsFetching,
  list: deviationsList,
});

const configurationsError = (state = null, action) => {
  switch (action.type) {
    case CONFIGURATIONS_FAILURE:
      return action.error;
    case CONFIGURATIONS_REQUEST:
    case CONFIGURATIONS_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const configurationsIsFetching = (state = false, action) => {
  switch (action.type) {
    case CONFIGURATIONS_REQUEST:
      return true;
    case CONFIGURATIONS_SUCCESS:
    case CONFIGURATIONS_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const configurationsList = (state = [], action) => {
  switch (action.type) {
    case CONFIGURATIONS_SUCCESS:
      return action.apartmentConfigurations;
    case CONFIGURATIONS_REQUEST:
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const configurations = combineReducers({
  error: configurationsError,
  isFetching: configurationsIsFetching,
  list: configurationsList,
});

const copyDialogVisibility = (state = false, action) => {
  switch (action.type) {
    case TOGGLE_COPY_DIALOG:
      return !!action.show;
    case CREATE_SUCCESS:
      return false;
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const fileRenameDialogVisibility = (state = false, action) => {
  switch (action.type) {
    case TOGGLE_RENAME_DIALOG:
      return action.show;
    case FILE_UPDATE_SUCCESS:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const apartmentView = combineReducers({
  apartment,
  project,
  deviations,
  deviation,
  configurations,
  copyDialogVisibility,
  fileRenameDialogVisibility,
});

export default apartmentView;



// WEBPACK FOOTER //
// ./src/reducers/apartmentView.js